/**
 * High-Performance Vehicle Cluster Manager
 * 
 * Implements marker clustering and viewport-based rendering without external dependencies
 * Handles 1000+ vehicles with smooth performance
 * 
 * Features:
 * - Spatial clustering based on zoom level
 * - Viewport-based culling (only render visible markers)
 * - Icon caching and reuse
 * - Dynamic cluster sizing
 * - Memory-efficient rendering
 */

import React, { useMemo, useState, useEffect } from 'react';
import { Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';

class VehicleClusterManager {
  constructor() {
    this.iconCache = new Map();
    this.clusterCache = new Map();
    this.viewportBounds = null;
    this.lastZoom = 0;
    this.performanceMetrics = {
      clustersCreated: 0,
      markersRendered: 0,
      markersCulled: 0,
      renderTime: 0,
      lastUpdate: Date.now()
    };
  }

  /**
   * Check if a bus or van should be hidden due to inactivity (1+ hour)
   * @param {Object} vehicle - Vehicle object
   * @param {boolean} isUserOwned - Whether the vehicle is owned by the current user
   * @returns {boolean} True if vehicle should be hidden
   */
  shouldHideInactiveVehicle(vehicle, isUserOwned = false) {
    // Never hide vehicles owned by the user
    if (isUserOwned) {
      return false;
    }

    // Only apply to buses, vans, and trucks (garbage trucks)
    if (vehicle.vehicle_type !== 'bus' && vehicle.vehicle_type !== 'van' && vehicle.vehicle_type !== 'truck') {
      return false;
    }

    if (!vehicle.last_updated) return false;

    const now = new Date();
    let updateTime;

    if (typeof vehicle.last_updated === 'string') {
      if (vehicle.last_updated.endsWith('Z')) {
        updateTime = new Date(vehicle.last_updated);
      } else if (vehicle.last_updated.includes('+00:00')) {
        updateTime = new Date(vehicle.last_updated);
      } else if (vehicle.last_updated.includes('T')) {
        updateTime = new Date(vehicle.last_updated + 'Z');
      } else {
        updateTime = new Date(vehicle.last_updated);
      }
    } else {
      updateTime = new Date(vehicle.last_updated);
    }

    if (isNaN(updateTime.getTime())) return false;

    const diffMilliseconds = now.getTime() - updateTime.getTime();
    const diffMinutes = Math.floor(diffMilliseconds / (1000 * 60));

    // Hide if inactive for 60+ minutes (1 hour)
    return diffMinutes >= 60;
  }

  /**
   * Create cached icons for different vehicle types and states
   */
  getOrCreateIcon(vehicleType, isSelected = false, clusterSize = null, heading = null, speed = 0) {
    const iconKey = `${vehicleType}-${isSelected}-${clusterSize}-${heading !== null ? Math.round(heading) : 'none'}`;

    if (this.iconCache.has(iconKey)) {
      return this.iconCache.get(iconKey);
    }

    let icon;

    if (clusterSize) {
      // Cluster icon
      const size = Math.min(40 + Math.log(clusterSize) * 5, 60);
      const color = (vehicleType === 'bus' || vehicleType === 'truck') ? '#0d9488' : '#3b82f6'; // teal for garbage trucks, blue for vehicles

      icon = L.divIcon({
        html: `
          <div style="
            width: ${size}px;
            height: ${size}px;
            background-color: ${color};
            border: 3px solid white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            color: white;
            font-size: ${Math.min(14, size / 3)}px;
            box-shadow: 0 2px 5px rgba(0,0,0,0.3);
          ">
            ${clusterSize}
          </div>
        `,
        className: 'vehicle-cluster-icon',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2]
      });
    } else {
      // Individual vehicle icon with direction indicator
      // Use garbage truck emoji for bus and truck types (garbage trucks in this app)
      const emoji = (vehicleType === 'bus' || vehicleType === 'truck') ? '🚛' : vehicleType === 'van' ? '🚐' : '🚗';
      const size = isSelected ? 44 : 40;
      const borderColor = isSelected ? '#ef4444' : '#ffffff';

      // Show direction arrow for buses, trucks and vans at any speed > 0.5 km/h, others need > 1 km/h
      const isBusOrVan = vehicleType === 'bus' || vehicleType === 'van' || vehicleType === 'truck';
      const speedThreshold = isBusOrVan ? 0.5 : 1;
      const showDirection = heading !== null && speed > speedThreshold;
      const rotation = heading !== null ? heading : 0;

      // Calculate arrow position based on heading (place on edge in direction of travel)
      const radius = size / 2 + 10; // Place arrow 10px from edge
      const radians = (rotation - 90) * Math.PI / 180; // -90 to adjust for CSS coordinate system
      const arrowX = radius * Math.cos(radians);
      const arrowY = radius * Math.sin(radians);

      icon = L.divIcon({
        html: `
          <div style="
            width: ${size}px;
            height: ${size}px;
            position: relative;
            ${isSelected ? 'animation: pulse 2s infinite;' : ''}
          ">
            ${showDirection ? `
              <div style="
                position: absolute;
                left: 50%;
                top: 50%;
                transform: translate(-50%, -50%) translate(${arrowX}px, ${arrowY}px);
                width: 20px;
                height: 20px;
                z-index: 10;
              ">
                <svg width="20" height="20" viewBox="0 0 16 16" style="transform: rotate(${rotation}deg); filter: drop-shadow(0 2px 4px rgba(0,0,0,0.6));">
                  <polygon points="8,1 14,14 8,11 2,14" fill="#ef4444" stroke="#dc2626" stroke-width="1"/>
                </svg>
              </div>
            ` : ''}
            <div style="
              width: ${size}px;
              height: ${size}px;
              background-color: white;
              border: 2px solid ${borderColor};
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: ${size / 2.5}px;
              box-shadow: 0 2px 5px rgba(0,0,0,0.3);
            ">
              ${emoji}
            </div>
          </div>
        `,
        className: 'vehicle-marker-icon',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2]
      });
    }

    this.iconCache.set(iconKey, icon);
    return icon;
  }

  /**
   * Calculate clustering distance based on zoom level
   */
  getClusterDistance(zoom) {
    // Adjust clustering distance based on zoom level
    // Higher zoom = less clustering, lower zoom = more clustering
    if (zoom >= 16) return 30;   // Very detailed view - minimal clustering
    if (zoom >= 14) return 50;   // City view
    if (zoom >= 12) return 80;   // District view
    if (zoom >= 10) return 120;  // Region view
    return 200;                  // Country view - maximum clustering
  }

  /**
   * Check if point is within viewport bounds (with padding)
   */
  isInViewport(lat, lng, bounds, padding = 0.1) {
    if (!bounds) return true;

    const latPadding = (bounds.north - bounds.south) * padding;
    const lngPadding = (bounds.east - bounds.west) * padding;

    return lat >= bounds.south - latPadding &&
      lat <= bounds.north + latPadding &&
      lng >= bounds.west - lngPadding &&
      lng <= bounds.east + lngPadding;
  }

  /**
   * Cluster vehicles using spatial grid algorithm
   */
  clusterVehicles(vehicles, zoom, bounds, userVehicleIds = new Set()) {
    const startTime = performance.now();

    const cacheKey = `${zoom}-${vehicles.length}-${bounds ? `${bounds.north}-${bounds.south}-${bounds.east}-${bounds.west}` : 'no-bounds'}`;

    // Use cached result if vehicles and view haven't changed significantly
    if (this.clusterCache.has(cacheKey)) {
      const cached = this.clusterCache.get(cacheKey);
      if (Date.now() - cached.timestamp < 1000) { // 1 second cache
        return cached.result;
      }
    }

    const clusterDistance = this.getClusterDistance(zoom);
    const clusters = [];
    const processed = new Set();

    // First pass: filter vehicles in viewport and only active vehicles
    const visibleVehicles = vehicles.filter(vehicle => {
      if (!vehicle.last_location) return false;
      if (vehicle.is_active === false) return false; // Filter out inactive vehicles

      // Check if vehicle is user-owned
      const isUserOwned = userVehicleIds.has(vehicle.id);

      // Hide buses/vans that haven't moved for 1+ hour (unless user-owned)
      if (this.shouldHideInactiveVehicle(vehicle, isUserOwned)) return false;

      return this.isInViewport(
        vehicle.last_location.latitude,
        vehicle.last_location.longitude,
        bounds,
        0.1 // 10% padding around viewport
      );
    });

    this.performanceMetrics.markersCulled = vehicles.length - visibleVehicles.length;

    // Second pass: cluster visible vehicles
    for (const vehicle of visibleVehicles) {
      if (processed.has(vehicle.id)) continue;

      const cluster = {
        id: `cluster_${clusters.length}`,
        lat: vehicle.last_location.latitude,
        lng: vehicle.last_location.longitude,
        vehicles: [vehicle],
        vehicleType: vehicle.vehicle_type
      };

      // Find nearby vehicles to cluster
      for (const otherVehicle of visibleVehicles) {
        if (processed.has(otherVehicle.id) || otherVehicle.id === vehicle.id) continue;

        const distance = this.calculatePixelDistance(
          vehicle.last_location.latitude, vehicle.last_location.longitude,
          otherVehicle.last_location.latitude, otherVehicle.last_location.longitude,
          zoom
        );

        if (distance <= clusterDistance && otherVehicle.vehicle_type === vehicle.vehicle_type) {
          cluster.vehicles.push(otherVehicle);
          processed.add(otherVehicle.id);

          // Update cluster center (centroid)
          cluster.lat = cluster.vehicles.reduce((sum, v) => sum + v.last_location.latitude, 0) / cluster.vehicles.length;
          cluster.lng = cluster.vehicles.reduce((sum, v) => sum + v.last_location.longitude, 0) / cluster.vehicles.length;
        }
      }

      processed.add(vehicle.id);
      clusters.push(cluster);
    }

    const renderTime = performance.now() - startTime;
    this.performanceMetrics.clustersCreated = clusters.length;
    this.performanceMetrics.markersRendered = clusters.length;
    this.performanceMetrics.renderTime = renderTime;
    this.performanceMetrics.lastUpdate = Date.now();

    // Cache the result
    const result = { clusters, metrics: { ...this.performanceMetrics } };
    this.clusterCache.set(cacheKey, { result, timestamp: Date.now() });

    // Limit cache size
    if (this.clusterCache.size > 10) {
      const oldestKey = this.clusterCache.keys().next().value;
      this.clusterCache.delete(oldestKey);
    }

    //

    return result;
  }

  /**
   * Calculate approximate pixel distance between two points at given zoom
   */
  calculatePixelDistance(lat1, lng1, lat2, lng2, zoom) {
    const earthRadius = 6371000; // Earth radius in meters
    const metersPerPixel = (earthRadius * Math.cos(lat1 * Math.PI / 180) * 2 * Math.PI) / (256 * Math.pow(2, zoom));

    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distanceInMeters = earthRadius * c;

    return distanceInMeters / metersPerPixel;
  }

  /**
   * Clear all caches
   */
  clearCache() {
    this.iconCache.clear();
    this.clusterCache.clear();
  }

  /**
   * Get performance metrics
   */
  getMetrics() {
    return { ...this.performanceMetrics };
  }
}

// Create singleton instance
const clusterManager = new VehicleClusterManager();

/**
 * Optimized Vehicle Markers Component
 * Uses clustering and viewport culling for high performance
 */
export const OptimizedVehicleMarkers = ({
  vehicles = [],
  selectedVehicleId,
  onVehicleSelect,
  getTimeSinceUpdate,
  maxMarkersBeforeClustering = 50,
  userVehicles = []
}) => {
  const map = useMap();
  const [mapBounds, setMapBounds] = useState(null);
  const [zoom, setZoom] = useState(13);

  // Update viewport bounds and zoom when map moves
  useEffect(() => {
    const updateMapState = () => {
      const bounds = map.getBounds();
      setMapBounds({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest()
      });
      setZoom(map.getZoom());
    };

    // Initial update
    updateMapState();

    // Listen for map events
    map.on('moveend', updateMapState);
    map.on('zoomend', updateMapState);

    return () => {
      map.off('moveend', updateMapState);
      map.off('zoomend', updateMapState);
    };
  }, [map]);

  // Cluster vehicles based on current viewport and zoom
  const { clusters, metrics } = useMemo(() => {
    if (!vehicles.length) return { clusters: [], metrics: {} };

    // Create a Set of user vehicle IDs for quick lookup
    const userVehicleIds = new Set(userVehicles.map(v => v.id));

    // For small numbers of vehicles, don't cluster
    if (vehicles.length <= maxMarkersBeforeClustering && zoom >= 14) {
      const visibleVehicles = vehicles.filter(vehicle => {
        if (!vehicle.last_location) return false;
        if (vehicle.is_active === false) return false; // Filter out inactive vehicles

        // Check if vehicle is user-owned
        const isUserOwned = userVehicleIds.has(vehicle.id);

        // Hide buses/vans that haven't moved for 1+ hour (unless user-owned)
        if (clusterManager.shouldHideInactiveVehicle(vehicle, isUserOwned)) return false;

        return clusterManager.isInViewport(
          vehicle.last_location.latitude,
          vehicle.last_location.longitude,
          mapBounds
        );
      });

      return {
        clusters: visibleVehicles.map(vehicle => ({
          id: vehicle.id,
          lat: vehicle.last_location.latitude,
          lng: vehicle.last_location.longitude,
          vehicles: [vehicle],
          vehicleType: vehicle.vehicle_type
        })),
        metrics: { clustersCreated: visibleVehicles.length, renderTime: 0 }
      };
    }

    return clusterManager.clusterVehicles(vehicles, zoom, mapBounds, userVehicleIds);
  }, [vehicles, zoom, mapBounds, selectedVehicleId, maxMarkersBeforeClustering, userVehicles]);

  // Render clusters as markers
  return (
    <>
      {clusters.map(cluster => {
        const isCluster = cluster.vehicles.length > 1;
        const isSingleSelected = cluster.vehicles.length === 1 && cluster.vehicles[0].id === selectedVehicleId;
        const hasSelectedVehicle = cluster.vehicles.some(v => v.id === selectedVehicleId);

        // Get heading and speed for direction indicator
        const vehicle = cluster.vehicles[0];
        const heading = vehicle?.last_location?.heading;
        const speed = vehicle?.last_location?.speed || 0;

        const icon = clusterManager.getOrCreateIcon(
          cluster.vehicleType,
          isSingleSelected,
          isCluster ? cluster.vehicles.length : null,
          isCluster ? null : heading,
          isCluster ? 0 : speed
        );

        return (
          <Marker
            key={isCluster ? cluster.id : `${cluster.vehicles[0].id}-${cluster.vehicles[0].last_updated || Date.now()}`}
            position={[cluster.lat, cluster.lng]}
            icon={icon}
            eventHandlers={{
              click: () => {
                if (isCluster) {
                  // For clusters, zoom in to show individual vehicles
                  map.setView([cluster.lat, cluster.lng], Math.min(zoom + 2, 18));
                } else {
                  // For individual vehicles, select them
                  onVehicleSelect(cluster.vehicles[0].id);
                }
              }
            }}
          >
            <Popup>
              {isCluster ? (
                // Cluster popup
                <div className="text-sm">
                  <h3 className="font-bold text-gray-900 mb-2">
                    {cluster.vehicles.length} {(cluster.vehicleType === 'bus' || cluster.vehicleType === 'truck') ? 'Trucks' : 'Vehicles'}
                  </h3>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {cluster.vehicles.slice(0, 5).map(vehicle => (
                      <div key={vehicle.id} className="text-xs p-1 bg-gray-50 rounded cursor-pointer hover:bg-gray-100"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent popup from interfering
                          onVehicleSelect(vehicle.id);
                          // Center map on the selected vehicle with appropriate zoom
                          map.setView([vehicle.last_location.latitude, vehicle.last_location.longitude], Math.max(zoom, 16));
                        }}>
                        <div className="font-medium">
                          {vehicle.vehicle_type === 'bus' || vehicle.vehicle_type === 'van' || vehicle.vehicle_type === 'truck'
                            ? `${vehicle.make} ${vehicle.model}`
                            : `${vehicle.year} ${vehicle.make} ${vehicle.model}`
                          }
                        </div>
                        <div className="text-gray-600">{vehicle.license_plate}</div>
                        <div className="text-gray-500 text-xs">{getTimeSinceUpdate(vehicle.last_updated)}</div>
                      </div>
                    ))}
                    {cluster.vehicles.length > 5 && (
                      <div className="text-xs text-gray-500 text-center py-1">
                        ... and {cluster.vehicles.length - 5} more
                      </div>
                    )}
                  </div>
                  <div className="mt-2 text-xs text-gray-500 border-t pt-2">
                    Click cluster to zoom in, or click individual vehicles above to select and track them
                  </div>
                </div>
              ) : (
                // Individual vehicle popup (simplified for performance)
                <VehiclePopup
                  vehicle={cluster.vehicles[0]}
                  selectedVehicleId={selectedVehicleId}
                  getTimeSinceUpdate={getTimeSinceUpdate}
                />
              )}
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

/**
 * Lightweight Vehicle Popup Component
 */
const VehiclePopup = ({ vehicle, selectedVehicleId, getTimeSinceUpdate }) => {
  const heading = vehicle?.last_location?.heading;
  const speed = vehicle?.last_location?.speed || 0;
  const speedMph = Math.round(speed * 0.621371);

  // Get direction label from heading
  const getDirectionLabel = (heading) => {
    if (heading === null || heading === undefined) return 'Unknown';
    if (heading >= 337.5 || heading < 22.5) return 'North ↑';
    if (heading >= 22.5 && heading < 67.5) return 'NE ↗';
    if (heading >= 67.5 && heading < 112.5) return 'East →';
    if (heading >= 112.5 && heading < 157.5) return 'SE ↘';
    if (heading >= 157.5 && heading < 202.5) return 'South ↓';
    if (heading >= 202.5 && heading < 247.5) return 'SW ↙';
    if (heading >= 247.5 && heading < 292.5) return 'West ←';
    if (heading >= 292.5 && heading < 337.5) return 'NW ↖';
    return 'Unknown';
  };

  // Calculate time since last update for parking status
  const getStatusDisplay = (timestamp) => {
    if (!timestamp) return getTimeSinceUpdate(timestamp);

    const now = new Date();
    let updateTime;

    if (typeof timestamp === 'string') {
      if (timestamp.endsWith('Z')) {
        updateTime = new Date(timestamp);
      } else if (timestamp.includes('+00:00')) {
        updateTime = new Date(timestamp);
      } else if (timestamp.includes('T')) {
        updateTime = new Date(timestamp + 'Z');
      } else {
        updateTime = new Date(timestamp);
      }
    } else {
      updateTime = new Date(timestamp);
    }

    if (isNaN(updateTime.getTime())) {
      return getTimeSinceUpdate(timestamp);
    }

    const diffMilliseconds = now.getTime() - updateTime.getTime();
    const diffMinutes = Math.floor(diffMilliseconds / (1000 * 60));

    // If more than 15 minutes, show as "Parked"
    if (diffMinutes >= 15) {
      if (diffMinutes < 60) {
        return `🅿️ Parked for ${diffMinutes} mins`;
      } else {
        const hours = Math.floor(diffMinutes / 60);
        const remainingMins = diffMinutes % 60;
        if (hours === 1 && remainingMins === 0) {
          return `🅿️ Parked for 1 hour`;
        } else if (remainingMins === 0) {
          return `🅿️ Parked for ${hours} hours`;
        } else {
          return `🅿️ Parked for ${hours}h ${remainingMins}m`;
        }
      }
    }

    // Less than 15 minutes, show normal time
    return getTimeSinceUpdate(timestamp);
  };

  return (
    <div className="text-xs min-w-32 max-w-48">
      <div className="flex justify-between items-start mb-2">
        <h3 className="font-bold text-sm text-gray-900">
          {vehicle.vehicle_type === 'bus' || vehicle.vehicle_type === 'van' || vehicle.vehicle_type === 'truck'
            ? `${vehicle.make} ${vehicle.model}`
            : `${vehicle.year} ${vehicle.make} ${vehicle.model}`
          }
        </h3>
        <span className={`px-1 py-0.5 rounded text-xs ${vehicle.id === selectedVehicleId ? 'bg-red-100 text-red-800' :
          (vehicle.vehicle_type === 'bus' || vehicle.vehicle_type === 'truck') ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
          }`}>
          {vehicle.id === selectedVehicleId && '🎯'}
          {vehicle.id !== selectedVehicleId && ((vehicle.vehicle_type === 'bus' || vehicle.vehicle_type === 'truck') ? '🚛' : vehicle.vehicle_type === 'van' ? '🚐' : '🚗')}
        </span>
      </div>

      <div className="space-y-1">
        <div className="flex justify-between">
          <span className="text-gray-600">License:</span>
          <span className="font-medium">{vehicle.license_plate}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Color:</span>
          <span>{vehicle.color}</span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-600">Speed:</span>
          <span className="font-medium">{speedMph} mph</span>
        </div>

        {heading !== null && heading !== undefined && (
          <div className="flex justify-between">
            <span className="text-gray-600">Direction:</span>
            <span className="font-medium text-red-600">{getDirectionLabel(heading)}</span>
          </div>
        )}

        <div className="pt-1 border-t border-gray-200 text-gray-500 text-xs">
          {getStatusDisplay(vehicle.last_updated)}
        </div>
      </div>
    </div>
  );
};

export default clusterManager;